import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { useDebounce } from "use-debounce";
import ClipLoader from "react-spinners/ClipLoader";
import { Dialog, Button, Image, TextInput, Anchor } from "hesus-ui-kit";
import PulseLoader from "react-spinners/PulseLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { handleApiError } from "@utils/errorHandler";
import { address, addressSingleLineWithoutCountry } from "@utils/utils";
import { RequiredMessage } from "@utils/extra-style";
import companyLogo from "@icons/Company.svg";
import closeIcon from "@icons/ic-close-white.svg";
import searchIcon from "@icons/ic-search-light-blue.svg";
import editIcon from "@icons/ic-green-edit.svg";
import greyEditIcon from "@icons/ic-validate-grey.svg";
import Header from "./Header";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  FlexRow,
  SectionContainer,
  ImageDiv,
  CompanyContainer,
  GridRow,
  SubTitle,
  RowHeader,
  GridItem,
  GridItemImage,
} from "./LeadCompany.styled";

const LeadCompany = (props) => {
  const { t, onClose, leadId, companyInfo, setIsRefreshList, leadCountry } =
    props;
  const [isLoading, setIsLoading] = useState(false);
  const [isLeadValidating, setIsLeadValidating] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyError, setSelectedCompanyError] = useState("");
  const [companyList, setCompanyList] = useState([]);

  const [search, setSearch] = useState(companyInfo?.name ?? "");
  const [debounceQuery] = useDebounce(search, 700);
  const countryId = companyInfo?.platformCountry?.id
    ? companyInfo?.platformCountry?.id
    : leadCountry;
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;
  const fetchMatchedCompany = () => {
    setIsLoading(true);
    axios
      .get(
        `/general/search?search=${search}&limit=50&orderBy=asc&orderColumn=name&countries=[${countryId}]&objectTypes=["company"]&dependancies=["address"]`
      )
      .then((response) => {
        setCompanyList(response.data?.companies);
        setIsLoading(false);
      })
      .catch((error) => handleApiError(error));
  };

  const getFormData = () => {
    const data = {
      status: 2,
      step: 1,
      company: JSON.stringify({
        id: selectedCompany,
      }),
    };
    return data;
  };

  const validateForm = () => {
    let isFormValid = true;
    if (!selectedCompany || selectedCompany === "") {
      isFormValid = false;
      setSelectedCompanyError(t("LEAD_COMPANY_SELECTION_ERROR_MESSAGE"));
    }
    return isFormValid;
  };

  const onValidateLead = () => {
    const isFormValid = validateForm();
    if (isFormValid) {
      setIsLeadValidating(true);
      axios
        .put(`/leads/${leadId}`, getFormData())
        .then((response) => {
          setIsLeadValidating(false);
          onClose();
          setIsRefreshList(true);
        })
        .catch((error) => handleApiError(error));
    }
  };

  useEffect(() => {
    fetchMatchedCompany();
  }, [debounceQuery]);

  const onCompanySearch = (e) => {
    setSearch(e.target.value);
  };
  const updateSelectedCompany = (id) => {
    setSelectedCompany(id);
    setSelectedCompanyError("");
  };
  const onCompanyCreation = () => {
    window.open(`/company/create?leadId=${leadId}`, "_self");
  };

  return (
    <Dialog width="65rem">
      <div
        className="lead-view-company"
        style={{ border: "6px solid #767676" }}
      >
        <HeaderWrapper>
          <Title>{t("LEADS_COMPANY_SELECTION_POPUP")}</Title>

          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <SectionContainer>
            <CompanyContainer>
              <FlexRow marginBottom="10px">
                <ImageDiv>
                  <Image
                    src={companyLogo}
                    alt="Logo icon"
                    styles={{ width: "22px", margin: "5px 0px 0px 6px" }}
                  />
                </ImageDiv>
                <Title color="#0A4986" fontSize="14px">
                  {t("LEAD_COMPANY_SELECTION_TITLE")}
                </Title>
              </FlexRow>
              <GridRow column="1fr 1fr">
                <FlexRow>
                  <SubTitle>{t("LEAD_COMPANY_NAME")} : </SubTitle>
                  <SubTitle color="#767676" fontWeight="400">
                    {companyInfo?.name ?? "-"}
                  </SubTitle>
                </FlexRow>
                <FlexRow>
                  <SubTitle>{t("LEAD_COMPANY_ADDRESS")} : </SubTitle>
                </FlexRow>
              </GridRow>
              <GridRow column="1fr 1fr">
                <FlexRow align="flex-start">
                  <SubTitle>{t("LEAD_COMPANY_REGISTRATION")} : </SubTitle>
                  <SubTitle color="#767676" fontWeight="400">
                    {companyInfo?.registrationNumber ?? "-"}
                  </SubTitle>
                </FlexRow>
                <FlexRow align="flex-start">
                  {" "}
                  <SubTitle color="#767676" fontWeight="400">
                    {companyInfo?.address && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: address(
                            countryId,
                            companyInfo?.address,
                            countryList
                          ),
                        }}
                      ></span>
                    )}
                    {!companyInfo?.address && "-"}
                  </SubTitle>
                </FlexRow>
              </GridRow>
            </CompanyContainer>

            <GridRow column="75% 23%">
              <FlexRow align="flex-end">
                <Title color="#474747" fontSize="14px" fontWeight="400">
                  {t("LEAD_COMPANY_SELECTION_INFORMATION")} :
                </Title>
              </FlexRow>
              <TextInput
                id="lead-company-search"
                type="text"
                value={String(search)}
                onChange={(e) => onCompanySearch(e)}
                placeholder={t("SEARCH")}
                inputStyles={{
                  border: "1px solid #767676 !important",
                  background: `url(${searchIcon}) no-repeat right`,
                  "background-size": "26px 16px",
                  padding: "7px 5px 7px 10px",
                  width: "100%",
                }}
              />
            </GridRow>
            {selectedCompanyError && (
              <GridRow column="1fr">
                <RequiredMessage maxWidth="auto">
                  {selectedCompanyError}
                </RequiredMessage>
              </GridRow>
            )}
            <RowHeader>
              <GridRow
                column="10% 30% 18% 35% 7%"
                marginBottom="0px"
                gap="0rem"
              >
                <Header title="ID" align="center" borderRadius="left" />
                <Header title="COMPANY_NAME" align="left" />
                <Header title="COMPANY_IDENTICATION_NUMBER" align="center" />
                <Header title="COMPANY_ADDRESS" align="left" />
                <Header title="" align="left" borderRadius="right" />
              </GridRow>
            </RowHeader>
            {companyList.map((company) => {
              return (
                <GridRow
                  column="10% 30% 18% 35% 7%"
                  marginBottom="0px"
                  gap="0rem"
                  borderBottom="solid 1px #cccccc"
                  key={"lead-company-" + company.id}
                  backgroundColor={
                    selectedCompany === company.id ? "#ccc" : "#fff"
                  }
                >
                  <GridItem
                    align="center"
                    alignContent="center"
                    backgroundColor={
                      selectedCompany === company.id ? "#ccc" : "#fff"
                    }
                    color="#004C7F"
                  >
                    <Anchor
                      url={`/company/view/${company.id}/general-info`}
                      target="_blank"
                      rel="noreferrer"
                      styles={{
                        textDecoration: "underline !important",
                      }}
                    >
                      {"#" + company.id}
                    </Anchor>
                  </GridItem>
                  <Header
                    title={company?.name}
                    align="left"
                    alignContent="center"
                    backgroundColor={
                      selectedCompany === company.id ? "#ccc" : "#fff"
                    }
                    color="#474747"
                  />
                  <Header
                    title={company?.registrationNumber ?? "-"}
                    align="center"
                    alignContent="center"
                    backgroundColor={
                      selectedCompany === company.id ? "#ccc" : "#fff"
                    }
                    color="#474747"
                    fontWeight="400"
                  />
                  <GridItem
                    fontSize="16px"
                    fontWeight="400"
                    backgroundColor={
                      selectedCompany === company.id ? "#ccc" : "#fff"
                    }
                    color="#474747"
                    textAlign="left"
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: addressSingleLineWithoutCountry(
                          countryId,
                          company?.shippingAddress
                        ),
                      }}
                    ></span>
                  </GridItem>
                  <GridItemImage
                    backgroundColor={
                      selectedCompany === company.id ? "#ccc" : "#fff"
                    }
                    textAlign="center"
                    styles={{ alignSelf: "center" }}
                  >
                    <Image
                      onClick={() => updateSelectedCompany(company.id)}
                      src={
                        selectedCompany === company.id ? editIcon : greyEditIcon
                      }
                      alt="editIcon"
                      styles={{ width: "auto", alignSelf: "center" }}
                    />
                  </GridItemImage>
                </GridRow>
              );
            })}
            {!isLoading && companyList?.length === 0 && (
              <GridRow>
                <GridItem color="#474747">
                  {t("LEAD_COMPANY_SEARCH_NO_RESULTS")}
                </GridItem>
              </GridRow>
            )}
            {isLoading && (
              <GridRow>
                <PulseLoader color="#1bace4" size={15} />
              </GridRow>
            )}
          </SectionContainer>
        </FormBody>

        <ButtonWrapper>
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              marginRight: "0.32rem",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CANCEL")}
          </Button>
          <Button
            styles={{
              color: "#1BACE4",
              background: "#fff",
              border: "2px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#fff",
              },
            }}
            onClick={onCompanyCreation}
          >
            {t("ADD_COMPANY")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onValidateLead}
          >
            <>
              {isLeadValidating && <ClipLoader size={15} color="#fff" />}
              {!isLeadValidating && t("VALIDATE")}
            </>
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

LeadCompany.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateDisputeRefeshStatus: PropTypes.func.isRequired,
  siteId: PropTypes.number.isRequired,
  planningId: PropTypes.number,
};

LeadCompany.defaultProps = {
  id: null,
  isEditMode: false,
  planningId: null,
};

export default withTranslation()(LeadCompany);
